import { Add, Delete, InfoOutlined } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { FC, useMemo, useState } from "react";

import TableSearchInput from "../../components/TableSearchInput";
import { useAppSelector } from "../../redux";
import { IGetUserRolesRequestData, IGetUserRolesResponse, PermissionDetailsType } from "../../types";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useDeleteUserRoleMutation, useGetUserRolesQuery } from "../../redux/reducers/rolesPermissions.reducer";
import { AGDataGrid } from "../../components/AGDataGrid";
import { ROWS_PER_PAGE } from "../../utils/constants";
import UserRoleDetailsDialog from "./UserRoleDetailsDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useNotification } from "../../components/FeedbackNotification/useNotification";

export interface IUserRoleRowDetails {
	id: number;
	sr_no: number;
	name: string;
	description: string;
	permissions: PermissionDetailsType;
	has_all_rto_access: boolean;
}

const UserRoles: FC = () => {
	const authState = useAppSelector((state) => state.auth);

	const { notify } = useNotification();

	const [filters, setFilters] = useState<IGetUserRolesRequestData>({});
	const [userRoleDetailsDialogOpen, setUserRoleDetailsDialogOpen] = useState<boolean>(false);
	const [selectedUserRole, setSelectedUserRole] = useState<IUserRoleRowDetails | null>(null);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);

	// APIS
	// GET USER ROLES
	const {
		data: getUserRolesResponse,
		isLoading: getUserRolesLoading,
		isFetching: getUserRolesFetching,
		refetch: refetchUserRoles
	} = useGetUserRolesQuery(filters);

	// DELETE USER ROLE
	const [deleteUserRole, { isLoading: deleteUserRoleLoading, reset: resetDeleteUserRoleState }] =
		useDeleteUserRoleMutation();

	const loggedInUserRole = useMemo<IGetUserRolesResponse | null>(
		() => getUserRolesResponse?.find((item) => item.id === authState.user.group_id) ?? null,
		[authState.user.group_id, getUserRolesResponse]
	);

	const readOnlyPermission = useMemo<boolean>(
		() => !authState.user.permissions || !authState.user.permissions.user_management?.edit,
		[authState.user.permissions]
	);

	const rowsData = useMemo<IUserRoleRowDetails[]>(() => {
		if (!getUserRolesResponse) return [];

		return getUserRolesResponse.map((userRole, index) => ({
			id: userRole.id,
			sr_no: index + 1,
			name: userRole.name,
			description: userRole.description ?? "",
			permissions: userRole.group_permissions,
			has_all_rto_access: userRole.has_all_rto_access
		}));
	}, [getUserRolesResponse]);

	function handleSubmitSearchInput(searchInput: string) {
		setFilters((prev) => {
			const updatedFilters = { ...prev };

			if (searchInput) {
				updatedFilters.search = searchInput;
			} else {
				delete updatedFilters.search;
			}

			return updatedFilters;
		});
	}

	function handleOpenUserRoleDetailsDialog(userRole: IUserRoleRowDetails | null) {
		setSelectedUserRole(userRole);
		setUserRoleDetailsDialogOpen(true);
	}

	function handleCloseUserRoleDetailsDialog() {
		setUserRoleDetailsDialogOpen(false);
		setSelectedUserRole(null);
		refetchUserRoles();
	}

	function handleOpenDeleteConfirmationDialog(userRole: IUserRoleRowDetails) {
		setSelectedUserRole(userRole);
		setShowDeleteConfirmationDialog(true);
	}

	function handleCloseDeleteConfirmationDialog() {
		setShowDeleteConfirmationDialog(false);
		setSelectedUserRole(null);
	}

	function handleSubmitDeleteConfirmation() {
		if (selectedUserRole) {
			deleteUserRole(selectedUserRole.id)
				.unwrap()
				.then(() => {
					handleCloseDeleteConfirmationDialog();
					refetchUserRoles();
				})
				.catch((error) => {
					notify("data" in error && error.data ? String(error.data) : "Failed to delete user role", "error");
				})
				.finally(() => {
					resetDeleteUserRoleState();
				});
		}
	}

	const columnsData: GridColDef[] = [
		{
			field: "sr_no",
			headerName: "Sr. No.",
			width: 65,
			sortable: false,
			headerAlign: "center",
			align: "center"
		},
		{
			field: "name",
			headerName: "Name",
			flex: 1,
			minWidth: 120,
			sortable: false
		},
		{
			field: "description",
			headerName: "Description",
			flex: 2,
			minWidth: 200,
			sortable: false
		},
		{
			field: "actions",
			headerName: "",
			width: readOnlyPermission ? 50 : 75,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IUserRoleRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					{!readOnlyPermission ? (
						<IconButton color="error" size="small" onClick={() => handleOpenDeleteConfirmationDialog(params.row)}>
							<Delete fontSize="inherit" />
						</IconButton>
					) : null}

					<IconButton color="info" size="small" onClick={() => handleOpenUserRoleDetailsDialog(params.row)}>
						<InfoOutlined fontSize="inherit" />
					</IconButton>
				</Box>
			)
		}
	];

	return (
		<Box className="page-content-wrapper">
			<Box className="page-header-wrapper">
				<TableSearchInput showClearButton={!!filters.search} onSubmit={handleSubmitSearchInput} />

				{!readOnlyPermission ? (
					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<Add />}
						onClick={() => handleOpenUserRoleDetailsDialog(null)}
					>
						Add User Role
					</Button>
				) : null}
			</Box>

			<Box className="page-content">
				<AGDataGrid
					hideLastSeparator
					columns={columnsData}
					rows={rowsData}
					paginationMode="client"
					loading={getUserRolesLoading || getUserRolesFetching}
					hideFooter={(getUserRolesResponse?.length ?? 0) <= ROWS_PER_PAGE}
				/>
			</Box>

			<UserRoleDetailsDialog
				open={userRoleDetailsDialogOpen}
				userRoleDetails={selectedUserRole}
				allRTOAccessDisabled={!loggedInUserRole || !loggedInUserRole.has_all_rto_access}
				onClose={handleCloseUserRoleDetailsDialog}
			/>

			<ConfirmationDialog
				open={showDeleteConfirmationDialog}
				onClose={handleCloseDeleteConfirmationDialog}
				onConfirm={handleSubmitDeleteConfirmation}
				confirmButtonLoading={deleteUserRoleLoading}
				title="Delete User Role"
				heading={`Are you sure you want to delete the user role "${selectedUserRole?.name}"?`}
				submitButtonText="Delete"
				cancelButtonText="Cancel"
				color="error"
				width="xs"
			/>
		</Box>
	);
};

export default UserRoles;
